<template>
  <v-autocomplete
    :id="`search-term-${searchId}`"
    v-model="selectedItem"
    class="lh--force-white-background"
    :items="items"
    :label="label + (required ? '*' : '')"
    placeholder="Start typing medication (at least 3 characters) or enter full NDC."
    :disabled="disabled"
    :clearable="true"
    :return-object="true"
    :allow-free-text-input="allowFreeTextInput"
    :loading="loading"
    :search="searchTerm"
    :hide-no-data="!showNoResultsMessage"
    :menu="shouldShowMenu"
    :menu-props="menuProps"
    @update:model-value="handleSelection"
    @update:search="handleSearch"
    @update:menu="
      (val) => {
        menuOpen = val && (items.length > 0 || showNoResultsMessage);
      }
    "
    @keyup.enter="handleEnterKey"
    @keyup.esc="cleanInput">
    <template #item="{ props: itemProps, item }">
      <v-list-item v-bind="itemProps">
        <v-list-item-title>
          {{ item.label }} <span v-if="item.value.nationalDrugCode"> [{{ item.value.nationalDrugCode }}] </span>
        </v-list-item-title>
      </v-list-item>
    </template>
    <template #no-data>
      <v-list-item v-if="showNoResultsMessage">
        <v-list-item-title class="text-caption text-medium-emphasis">
          No medication found. Enter full medication name and press Enter to continue.
        </v-list-item-title>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { mainServices } from "@/services/mainServices";
import debounce from "lodash.debounce";

const props = defineProps({
  data: {
    type: Object,
    required: false,
    default: () => ({
      label: "Please type at least 3 characters to search for medication or enter full NDC.",
      searchId: "medication-search",
    }),
  },
  required: {
    type: Boolean,
    required: false,
  },
  allowFreeTextInput: {
    type: Boolean,
    default() {
      return false;
    },
  },
  disabled: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const emit = defineEmits(["item-selected"]);

const selectedItem = ref(null);
const searchTerm = ref("");
const loading = ref(false);
const items = ref([]);
const menuOpen = ref(false);
const searchCompleted = ref(false);

const showNoResultsMessage = computed(
  () =>
    searchCompleted.value &&
    items.value.length === 0 &&
    searchTerm.value.trim().length >= 3 &&
    !loading.value &&
    props.allowFreeTextInput
);

const shouldShowMenu = computed(() => menuOpen.value && (items.value.length > 0 || showNoResultsMessage.value));

const label = computed(
  () => props.data?.label || "Please type at least 3 characters to search for medication or enter full NDC."
);
const searchId = computed(() => props.data?.searchId || "medication-search");

const menuProps = computed(() => ({
  contentClass: "medication-autocomplete-menu",
  maxHeight: 300,
  position: "absolute",
  transition: false,
  closeOnClick: true,
  closeOnContentClick: true,
  eager: items.value.length > 0,
  offsetY: true,
  offsetOverflow: true,
  location: "bottom",
}));

watch([items, showNoResultsMessage], ([newItems, showNoResults]) => {
  if (newItems.length === 0 && !showNoResults) {
    menuOpen.value = false;
  } else if (showNoResults) {
    menuOpen.value = true;
  }
});

const transformMedicationData = (medication) => {
  const label = medication.medication || "";
  return {
    title: label,
    value: {
      medication: medication.medication,
      strength: medication.strength || null,
      routeAdministration: medication.routeAdministration || null,
      nationalDrugCode: medication.nationalDrugCode || null,
      packageSizeUnit: medication.packageSizeUnit || null,
      name: medication.name || null,
    },
  };
};

const searchMedications = async (query) => {
  if (!query || query.trim().length === 0) {
    items.value = [];
    menuOpen.value = false;
    searchCompleted.value = false;
    return;
  }

  const trimmedQuery = query.trim();
  const isNdc = /^\d{11}$/.test(trimmedQuery);
  const isName = trimmedQuery.length >= 3;

  if (!isNdc && !isName) {
    items.value = [];
    menuOpen.value = false;
    searchCompleted.value = false;
    return;
  }

  loading.value = true;
  searchCompleted.value = false;
  try {
    const results = await mainServices.searchMedications(trimmedQuery);
    items.value = results.map(transformMedicationData);
    searchCompleted.value = true;
    const shouldShowNoResults = items.value.length === 0 && props.allowFreeTextInput;
    menuOpen.value = items.value.length > 0 || shouldShowNoResults;
  } catch (error) {
    items.value = [];
    searchCompleted.value = true;
    const shouldShowNoResults = props.allowFreeTextInput;
    menuOpen.value = shouldShowNoResults;
  } finally {
    loading.value = false;
  }
};

const debouncedSearch = debounce(searchMedications, 500);

function handleSearch(value) {
  searchTerm.value = value;
  debouncedSearch(value);
}

function handleSelection(item) {
  if (item) {
    emit("item-selected", item);
  }
}

function handleEnterKey(event) {
  if (props.allowFreeTextInput && event.target.value) {
    const freeTextItem = {
      label: event.target.value,
      value: { medication: event.target.value },
    };
    emit("item-selected", freeTextItem);
  }
}

function cleanInput() {
  selectedItem.value = null;
  searchTerm.value = "";
  items.value = [];
  searchCompleted.value = false;
  menuOpen.value = false;
}
</script>

<style lang="scss" scoped>
@import "../styles/pages/_patient-information-form.scss";
@import "../styles/pages/_insurance-navigator-page";

.medication-autocomplete-menu {
  position: absolute !important;
  z-index: 1000;
  transform: none !important;
  will-change: transform;
  backface-visibility: hidden;
  perspective: 1000px;
}
</style>
